import { type TboAuditRow } from '@utazzo/shared';
import { UzButton, UzDrawer } from '@utazzo/ui';
import { ExternalLink } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { formatBytes, formatDateTimeDMY, OutcomeBadge, shortId } from '../tbo-audit.helpers';
import { useTboAuditBodyUrl } from '../use-tbo-audit';

/**
 * Read-only detail panel for a single TBO API call. Groups every column into
 * Identity / Call / TBO result sections, links the lead & itinerary back to
 * their pages, pretty-prints the summary gist, and (when a body was stored)
 * mints a short-lived presigned URL and opens the full gzipped payload in a new
 * tab.
 */

interface TboAuditDetailDrawerProps {
  row: TboAuditRow | null;
  open: boolean;
  onClose: () => void;
}

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-xs font-medium text-zinc-500">{label}</dt>
    <dd className="text-sm break-words text-zinc-900 tabular-nums">{children}</dd>
  </div>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <section className="mb-5">
    <h3 className="mb-2 text-xs font-semibold tracking-wide text-zinc-400 uppercase">{title}</h3>
    <dl className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</dl>
  </section>
);

const DASH = <span className="text-zinc-400">-</span>;

export const TboAuditDetailDrawer = ({
  row,
  open,
  onClose,
}: TboAuditDetailDrawerProps): React.JSX.Element => {
  const bodyUrl = useTboAuditBodyUrl();

  const openBody = (id: string): void => {
    bodyUrl.mutate(id, {
      onSuccess: (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
      },
      onError: () => toast.error('Could not load the full payload. The link may have expired.'),
    });
  };

  return (
    <UzDrawer
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="TBO call detail"
      description={row ? `${row.provider} · ${row.operation}` : undefined}
      contentClassName="sm:max-w-[560px]"
    >
      {row && (
        <div className="min-w-0">
          <Section title="Identity">
            <Field label="Source">{row.source}</Field>
            <Field label="Request id">{row.requestId ?? DASH}</Field>
            <Field label="User">
              {row.userName ? (
                <span title={row.userId ?? undefined}>{row.userName}</span>
              ) : row.userId ? (
                <span title={row.userId}>{shortId(row.userId)}</span>
              ) : (
                DASH
              )}
            </Field>
            <Field label="Lead">
              {row.leadId ? (
                <Link className="text-blue-600 hover:underline" to={`/leads/${row.leadId}`}>
                  {row.leadReference ?? row.leadId}
                </Link>
              ) : (
                DASH
              )}
            </Field>
            <Field label="Itinerary">
              {row.itineraryId ? (
                <Link
                  className="text-blue-600 hover:underline"
                  to={`/itineraries/${row.itineraryId}`}
                >
                  {row.itineraryTitle ?? row.itineraryId}
                </Link>
              ) : (
                DASH
              )}
            </Field>
          </Section>

          <Section title="Call">
            <Field label="Provider">{row.provider}</Field>
            <Field label="Operation">{row.operation}</Field>
            <Field label="Time">{formatDateTimeDMY(row.createdAt)}</Field>
            <Field label="Latency">{row.latencyMs != null ? `${row.latencyMs} ms` : DASH}</Field>
          </Section>

          <Section title="TBO result">
            <Field label="Outcome">
              <OutcomeBadge outcome={row.outcome} />
            </Field>
            <Field label="HTTP status">{row.httpStatus ?? DASH}</Field>
            <Field label="TBO response status">{row.tboResponseStatus ?? DASH}</Field>
            <Field label="TBO error code">{row.tboErrorCode ?? DASH}</Field>
            <Field label="Result count">{row.resultCount ?? DASH}</Field>
            <Field label="Trace id">{row.traceId ?? DASH}</Field>
            <Field label="Request size">{formatBytes(row.requestBytes)}</Field>
            <Field label="Response size">{formatBytes(row.responseBytes)}</Field>
          </Section>

          {row.tboErrorMessage && (
            <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-3 py-2">
              <p className="text-xs font-medium text-red-700">TBO error message</p>
              <p className="mt-0.5 text-sm break-words text-red-800">{row.tboErrorMessage}</p>
            </div>
          )}

          <section className="mb-5">
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-zinc-400 uppercase">
              Summary
            </h3>
            {row.summary != null ? (
              <pre className="max-h-64 overflow-auto rounded-md bg-zinc-50 p-3 text-xs text-zinc-800">
                {JSON.stringify(row.summary, null, 2)}
              </pre>
            ) : (
              <p className="text-sm text-zinc-400">No summary recorded.</p>
            )}
          </section>

          {row.hasBody && (
            <UzButton
              variant="secondary"
              onClick={() => openBody(row.id)}
              loading={bodyUrl.isPending}
              leftIcon={<ExternalLink className="h-4 w-4" />}
            >
              View full payload
            </UzButton>
          )}
        </div>
      )}
    </UzDrawer>
  );
};
